/**
 * The browser half of the MCP OAuth flow — `/oauth/authorize/{requests/:req,approve,deny}`.
 * Runs with the member's normal session; a session for a different workspace
 * than the AI app asked for gets `403 WORKSPACE_MISMATCH`.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import { getApiErrorCode } from '@clarix/utils';
import { unwrap, type ApiResponse } from './envelope';
import { queryKeys } from './keys';

export type OAuthBlockedReason = 'AI_CONNECTIONS_DISABLED' | 'NO_PROJECTS_ACCESS';

/** What the member lets the AI app do: read tools only, or read and change. */
export type OAuthGrantAccess = 'read' | 'read_write';

export type OAuthRequestDetails = {
  client_name: string;
  client_unverified: true;
  redirect_host: string;
  workspace: { slug: string; name: string };
  /** null when the account has no email on record (the backend sends `user?.email ?? null`). */
  user_email: string | null;
  blocked_reason: OAuthBlockedReason | null;
  /** Set when this app is already connected; the page preselects it. Older backends omit it. */
  current_access?: OAuthGrantAccess | null;
};

export type OAuthDecisionResult = { redirect_url: string };


/** GET /oauth/authorize/requests/:req — what the consent screen shows. */
export function useOAuthRequest(reqId: string | null) {
  return useQuery({
    queryKey: reqId ? queryKeys.oauthRequest(reqId) : queryKeys.oauthRequest('__nil__'),
    queryFn: async (): Promise<OAuthRequestDetails> => {
      const res = await getApiClient().get<ApiResponse<OAuthRequestDetails>>(
        `/oauth/authorize/requests/${encodeURIComponent(reqId ?? '')}`,
      );
      return unwrap(res.data);
    },
    enabled: !!reqId,
    // Expired, mismatched and blocked are terminal answers, not blips.
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function useOAuthDecision(action: 'approve' | 'deny') {
  return useMutation({
    mutationFn: async ({
      req,
      access,
    }: {
      req: string;
      /** Approve only. Omitted means `read_write` on the backend. */
      access?: OAuthGrantAccess;
    }): Promise<OAuthDecisionResult> => {
      const res = await getApiClient().post<ApiResponse<OAuthDecisionResult>>(
        `/oauth/authorize/${action}`,
        action === 'approve' && access ? { req, access } : { req },
      );
      return unwrap(res.data);
    },
  });
}

/** POST /oauth/authorize/approve — returns the AI app's redirect URL carrying the code. */
export const useApproveOAuthRequest = () => useOAuthDecision('approve');
/** POST /oauth/authorize/deny — returns the AI app's redirect URL carrying `access_denied`. */
export const useDenyOAuthRequest = () => useOAuthDecision('deny');

/** The code of a consent-flow error, plus the workspace a mismatch names. */
export function readOAuthError(err: unknown): {
  code: string | null;
  workspaceSlug: string | null;
} {
  const e = err as {
    details?: { workspace_slug?: unknown };
    response?: { data?: { error?: { details?: { workspace_slug?: unknown } } } };
  } | null;
  const slug = e?.response?.data?.error?.details?.workspace_slug ?? e?.details?.workspace_slug;
  const code =
    getApiErrorCode(err) ??
    ((err as { code?: unknown } | null)?.code as string | undefined) ??
    null;
  return {
    code: typeof code === 'string' ? code : null,
    workspaceSlug: typeof slug === 'string' ? slug : null,
  };
}
